import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingCart, Receipt, Wallet, FileBarChart, ClipboardList, AlertCircle, FileArchive, Coins,
} from "lucide-react";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const fmtDate = (d) =>
  d.toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function DashboardPage() {
  const { user, selectedStore, activeStoreId } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const params = activeStoreId ? `?store_id=${activeStoreId}` : "";
    let live = true;
    api.get(`/dashboard/stats${params}`).then((r) => { if (live) setStats(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [activeStoreId]);

  const storeName = selectedStore?.id === "all" ? "All Stores" : selectedStore?.name || "Select a store";
  const { phase2 } = useFeatures();

  const actions = [
    { label: "Record Purchase", icon: ShoppingCart, path: "/record-purchase", id: "record-purchase-card" },
    { label: "Record Expense", icon: Receipt, path: "/record-expense", id: "record-expense-card" },
    phase2 ? { label: "Record Payment", icon: Wallet, path: "/record-payment", id: "record-payment-card" }
      : { label: "Record Payment", icon: Wallet, path: null, id: "record-payment-card", phase: "Phase 2" },
    { label: "Daily Z Closing", icon: FileBarChart, path: phase2 ? "/z-closings" : null, id: "daily-z-closing-card", ...(phase2 ? {} : { phase: "Phase 2" }) },
    ...(phase2 ? [{ label: "Cash Closing", icon: Coins, path: "/cash-closings", id: "cash-closing-card" }] : []),
    { label: "My Submissions", icon: ClipboardList, path: "/submissions", id: "my-submissions-card", span: !phase2 },
    ...(["owner", "accountant", "store_manager"].includes(user?.role)
      ? [{ label: "Waiting for Review", icon: FileBarChart, path: "/review", id: "review-queue-card", span: true }] : []),
    ...(phase2 && ["owner", "accountant", "store_manager"].includes(user?.role)
      ? [{ label: "Store Reports", icon: FileBarChart, path: "/owner-reports", id: "owner-reports-card", span: true }] : []),
    ...(["owner", "accountant", "store_manager"].includes(user?.role)
      ? [{ label: "Reports & Backups", icon: FileArchive, path: "/reports", id: "reports-card", span: true }] : []),
    ...(user?.role === "owner"
      ? [{ label: "Staff Accounts", icon: FileArchive, path: "/staff", id: "staff-card", span: true }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-navy tracking-tight">
          {greeting()}, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-navy/45 text-sm mt-1">{storeName} &bull; {fmtDate(new Date())}</p>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-2.5">
          <StatPill value={stats.draft_purchases + stats.draft_expenses} label="Drafts" />
          <StatPill value={stats.today_purchases + stats.today_expenses} label="Today" gold />
          {stats.pending_approvals > 0
            ? <StatPill value={stats.pending_approvals} label="Pending" highlight onClick={() => navigate("/review")} testid="dashboard-pending-pill" />
            : <StatPill value={stats.approved_purchases + stats.approved_expenses} label="Approved" success />}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {actions.map((a) => (
          <button key={a.id} onClick={() => a.path && navigate(a.path)} disabled={!a.path}
            className={`gm-action-card flex flex-col items-start justify-between p-4 text-left ${a.span ? "col-span-2" : ""} ${!a.path ? "opacity-45 cursor-not-allowed" : ""}`}
            data-testid={a.id}>
            <a.icon className={`w-7 h-7 ${a.path ? "text-gold" : "text-navy/25"}`} />
            <div className="mt-3 w-full">
              <p className="font-semibold text-navy text-sm">{a.label}</p>
              {a.phase && <Badge variant="outline" className="mt-1 text-[10px] text-navy/35 border-navy/10">{a.phase}</Badge>}
            </div>
          </button>
        ))}
      </div>

      <Card className="border-navy/5 shadow-none">
        <CardContent className="p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          {phase2 ? (
            <button onClick={() => navigate("/z-closings")} className="text-sm text-navy/70 underline text-left" data-testid="dashboard-z-reminder">Remember to record each till's Z report at close</button>
          ) : (
            <>
              <p className="text-sm text-navy/60">Daily closing not yet completed</p>
              <Badge variant="outline" className="ml-auto text-[10px] border-navy/10 text-navy/35">Phase 2</Badge>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatPill({ value, label, gold, highlight, success, onClick, testid }) {
  let classes = "bg-white border border-navy/5";
  let vc = "text-navy";
  if (gold) vc = "text-gold";
  if (highlight) { classes = "bg-gold/10 border border-gold/20"; vc = "text-gold"; }
  if (success) vc = "text-emerald-600";
  return (
    <div className={`${classes} rounded-xl p-3 text-center ${onClick ? "cursor-pointer" : ""}`} onClick={onClick} role={onClick ? "button" : undefined} data-testid={testid}>
      <p className={`font-mono text-xl font-bold ${vc}`}>{value}</p>
      <p className="text-[11px] text-navy/45 mt-0.5">{label}</p>
    </div>
  );
}
